import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { CommonService } from '@app/shared/services';
import { environment } from '@env/environment';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  confirmSignIn,
  confirmSignUp,
  signUp,
  updateUserAttributes,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  type AuthUser,
  type SignInOutput,
  type ConfirmSignUpOutput,
  type UpdateUserAttributesOutput,
  type UserAttributeKey,
  fetchAuthSession,
  updateMFAPreference,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  fetchMFAPreference,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AppStorage } from '../cookies/app-storage';
import { FileDataObjectTransferService } from '../file-data-object-transfer/file-data-object-transfer.service';
import { AmplifyHttpClient } from './http.amplify-httpclient';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../user/user.service';
import { UserTypeEnum } from '@app/enum/UserTypeEnum';
import { LoginStoreEffects } from '@app/root-store/login-store/effects';
import { sessionStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user: BehaviorSubject<any>;
  public redirectUrl;
  subscription = new Subscription();

  constructor(
    @Inject(AppStorage) private appStorage: Storage,
    private router: Router,
    private fileDataObjectTransferService: FileDataObjectTransferService,
    private readonly httpService: AmplifyHttpClient,
    private http: HttpClient,
    private loginService: LoginStoreEffects,
    private commonService: CommonService,
    private userService: UserService
  ) {
    const authConfig: ResourcesConfig['Auth'] = {
      Cognito: {
        userPoolId: environment.userPoolId,
        userPoolClientId: environment.userPoolWebClientId,
        identityPoolId: environment.identityPoolId,
        signUpVerificationMethod: 'code',
        loginWith: {
          email: true,
          phone: true,
          username: true,
          oauth: {
            domain: environment.cognitoDomain, // e.g. your-app.auth.us-east-1.amazoncognito.com
            redirectSignIn: [environment.cognitoRedirectUrl], // Your frontend redirect URI
            redirectSignOut: [environment.baseUrl],
            responseType: 'code', // Recommended over 'token'
            scopes: [
              'email',
              'openid',
              'profile',
              'aws.cognito.signin.user.admin', // Add this scope
            ],
          },
        },
      },
    };
    const apiConfig: ResourcesConfig['API'] = {
      REST: {
        api: {
          endpoint: environment.apiUrl,
          region: environment.region,
        },
      },
    };

    Amplify.configure({
      Auth: authConfig,
      API: apiConfig,
    });

    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
    this._user = new BehaviorSubject(this.user);
    this.init();
  }

  private async updateUser() {
    try {
      const userInfo = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      this.commonService.eraseCookieFromAllPaths('user');
      const user: any = {};
      user.id = attributes['custom:userid'];
      user.email = attributes['email'];
      user.first_name = attributes['custom:first_name'];
      user.last_name = attributes['custom:last_name'];
      user.user_type = attributes['custom:user_type'];
      user.valid_payment_method = attributes['custom:valid_payment_method'];
      user.profile = attributes['profile'];
      this.appStorage.setItem('user', JSON.stringify(user));
      if (userInfo.username?.includes('google')) {
        this.appStorage.setItem('googleUser', JSON.stringify(user));
      }
      this._user.next(this.user);
    } catch (error) {
      this.appStorage.clear();
      this._user.next(null);
    }
  }

  public get user(): any {
    try {
      return JSON.parse(this.appStorage.getItem('user'));
    } catch {
      return {};
    }
  }

  public get googleUser(): any {
    try {
      return JSON.parse(this.appStorage.getItem('googleUser'));
    } catch {
      return {};
    }
  }

  public async refreshLocalUserData() {
    await this.updateUser();
  }

  public get $user(): Observable<any> {
    if (this._user) {
      return this._user.asObservable();
    } else {
      return new Observable((subscriber) => {
        subscriber.next(null);
      });
    }
  }

  public get $whenAuthenticated(): Observable<any> {
    this.updateUser();
    return new Observable((subscriber) => {
      // Store cleanup function returned by Hub.listen
      const hubListenerCleanup = Hub.listen('auth', (data) => {
        if (data.payload.event === 'signedIn') {
          subscriber.next('Authenticated');
        }
      });
      
      // Return teardown logic to clean up Hub listener when Observable is unsubscribed
      return () => {
        if (hubListenerCleanup) {
          hubListenerCleanup();
        }
      };
    });
  }

  public async isAuthenticated() {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  async init() {}

  async getUser() {
    return new Promise((resolve, reject) => {
      this.subscription.add(
        this.userService.getUser(false).subscribe((response) => {
          resolve(response);
        })
      );
    });
  }

  async login(emailAddress, password, fromSignIn, isNewUser = false, isredirect = true) {
    const loggedIn: any = {};
    loggedIn.success = false;

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: emailAddress,
        password,
        options: {
          authFlowType: 'CUSTOM_AUTH' as any, // ← This is mandatory!
        },
      });

      // const user = await getCurrentUser();
      // console.log('user', user);
      if (
        nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
        nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
        nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
      ) {
        loggedIn.success = true;
        loggedIn.challengeType = nextStep?.signInStep;
        // loggedIn.user = user;
        loggedIn.authUser = nextStep;
        return loggedIn;
      }

      await this.updateUser();

      if (!this.user.id) {
        throw 'Invalid user login, please try again or contact support';
      }

      loggedIn.success = true;
      // loggedIn.user = user;

      if (!isNewUser) {
        this.trackLogin(this.user);

        Hub.dispatch('auth', {
          event: 'signedIn',
          data: {},
          message: 'User is signed In',
        });

        if (isredirect) {
          if (this.redirectUrl && this.redirectUrl.startsWith('/home') && this.redirectUrl.includes('setupStep')) {
            const stepMatch = this.redirectUrl.match(/setupStep=([^&]+)/);
            const stepValue = stepMatch ? stepMatch[1] : '';
            this.router.navigateByUrl('/home', { state: { setupStep: stepValue } });
          } else if (this.redirectUrl && fromSignIn) {
            this.router.navigate([this.redirectUrl]);
          } else {
            const userDetails: any = await this.getUser();
            console.warn('userDetails', userDetails);
            if (userDetails?.user_type == UserTypeEnum.Professional) {
              this.router.navigateByUrl('/home', { state: { showLogin: true } });
            } else {
              if (this.router.url.includes('mobiledelivery')) {
                this.router.navigateByUrl('/mobiledelivery', { state: { showLogin: true } });
              } else {
                this.router.navigateByUrl('/home', { state: { showLogin: true } });
              }
            }
          }
        }
      }
    } catch (error) {
      loggedIn.success = false;
      loggedIn.error = error;
      return loggedIn;
    }
    return loggedIn;
  }

  async signUp(emailAddress, firstName, lastName, password, gender, marketing_emails, is_professinal) {
    const accountCreated: any = {};
    accountCreated.success = false;

    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: emailAddress,
        password: password,
        options: {
          userAttributes: {
            email: emailAddress,
            'custom:first_name': firstName,
            'custom:last_name': lastName,
            'custom:gender': gender ? gender : '',
            'custom:marketing_emails': marketing_emails,
            'custom:user_type': is_professinal,
            'custom:valid_payment_method': 'true',
          },
          autoSignIn: true,
        },
      });

      // In Amplify v6, isSignUpComplete will be false until email is confirmed
      accountCreated.success = true;
      accountCreated.userId = userId;
      accountCreated.nextStep = nextStep;
      accountCreated.isSignUpComplete = isSignUpComplete;
    } catch (error) {
      accountCreated.success = false;
      accountCreated.error = error.message || 'An error occurred during sign up';
      accountCreated.code = error.code || 'UnknownError';
      console.warn('Sign up error:', error);
    }

    return accountCreated;
  }

  async confirmCode(emailAddress, code) {
    let confirmed = false;
    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: emailAddress,
        confirmationCode: code,
      });

      // In Amplify v6, isSignUpComplete will be true after successful confirmation
      confirmed = isSignUpComplete;

      if (!confirmed) {
        console.warn('Confirmation not complete - user may need to sign in');
      }
    } catch (error) {
      console.warn('Confirm code error:', error);
      confirmed = false;
      throw error; // Re-throw to handle in the component
    }
    return confirmed;
  }

  async resendCode(emailAddress) {
    try {
      // For resending code, we need to use the same signUp call but with a new password
      const { isSignUpComplete, nextStep } = await signUp({
        username: emailAddress,
        password: '', // Empty password for resend
        options: {
          autoSignIn: false,
          userAttributes: {
            email: emailAddress,
          },
        },
      });

      return isSignUpComplete;
    } catch (error) {
      console.warn('Resend code error:', error);
      return false;
    }
  }

  logout(): Observable<any> {
    return new Observable((subscriber) => {
      signOut().then((res) => {
        this.commonService.eraseCookieFromAllPaths('user');
        this.commonService.eraseCookieFromAllPaths('professional');
        this.commonService.eraseCookieFromAllPaths('tcCode');
        localStorage.clear();
        sessionStorage.clear();
        this.fileDataObjectTransferService.sendMessage('isLogout');
        subscriber.next(res);
        this.appStorage.clear();
        this.appStorage.removeItem('professional');
        this.appStorage.removeItem('tcCode');
        this.appStorage.removeItem('signup');
      });
    });
  }

  async updateUserAttributes(user, attributes) {
    try {
      // First ensure we have a valid session
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('No valid session found');
      }

      // Update attributes using the correct method signature
      const result = await updateUserAttributes({
        userAttributes: attributes,
      });
      // Step 2: Ensure user context is available
      await getCurrentUser();

      // Step 3: Force refresh the token
      const sessions = await fetchAuthSession({ forceRefresh: true });

      const idToken = sessions.tokens?.idToken?.toString();
      if (idToken) {
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Update attributes error:', error);
      return false;
    }
  }

  async updateCurrentUserAttributes(attributes) {
    try {
      const result = await updateUserAttributes({ userAttributes: attributes });
      if (result) {
        await this.updateUser();
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  async enableCurrentUserMFA(mfaType: string) {
    // First enable the MFA type
    await updateMFAPreference({ [mfaType]: 'ENABLED' });

    // Get current MFA preferences
    const output = await fetchMFAPreference();

    const enabledTypes = output.preferred[0];
    if (enabledTypes.length === 0 || enabledTypes === 'undefined') {
      await updateMFAPreference({ [mfaType]: 'PREFERRED' });
    }

    return true;
  }

  async disableCurrentUserMFA(mfaType: string) {
    // Disable the selected MFA type
    const result = await this.updateCurrentUserMFA(mfaType);

    // Fetch current MFA preferences
    const output = await fetchMFAPreference();

    if (output && output.preferred && output.preferred.length > 0 && output.preferred[0] === 'undefined') {
      // Set the first remaining enabled type as preferred
      await updateMFAPreference({ [output.enabled[0].toLowerCase()]: 'PREFERRED' });
    }

    return result;
  }

  private async updateCurrentUserMFA(mfaType: string) {
    let enabled = false;
    try {
      const userInfo = await getCurrentUser();
      if (userInfo) {
        // MFA settings are now handled through Cognito User Pool settings
        await updateMFAPreference({ [mfaType]: 'DISABLED' });
      }
    } catch (error) {
      enabled = false;
    }
    return enabled;
  }

  async sendUserPhoneNumberVerification() {
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async confirmUserPhoneNumber(code: string) {
    try {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: code });
      await updateMFAPreference({ sms: 'ENABLED' });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
  async sendUserEmailVerification() {
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async confirmUserEmail(code: string) {
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async signInMFA(code) {
    const results: any = { success: false };

    try {
      const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: code });

      if (isSignedIn) {
        await this.updateUser();
        const user = this.getUser();
        if (!this.user?.id) throw 'Invalid user login, please try again or contact support';

        this.trackLogin(user);
        results.success = true;
        results.confirmation = { isSignedIn, nextStep };

        this.handlePostLoginRedirect(user);
      }
    } catch (error) {
      results.success = false;
      results.error = error;
    }

    return results;
  }

  handlePostLoginRedirect(user) {
    if (this.redirectUrl) {
      this.router.navigate([this.redirectUrl]);
      this.redirectUrl = null;
    } else if (this.router.url.includes('mobiledelivery')) {
      this.router.navigateByUrl('/mobiledelivery', {
        state: { showLogin: true },
      });
    } else if (user.user_type === 'Professional') {
      this.router.navigate(['/home']);
    } else {
      this.router.navigate(['/home']);
    }
  }

  async sendForgotPasswordEmail(email) {
    await resetPassword({ username: email });
    return true;
  }

  async confirmForgotPasswordCode(email, code, password) {
    const results: any = {};
    results.success = false;
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      });
      results.success = true;
      results.data = email;
    } catch (error) {
      results.success = false;
      results.error = error;
    }
    return results;
  }

  async changeUserPassword(oldPassword, newPassword) {
    const results: any = {};
    results.success = false;
    try {
      await updatePassword({
        oldPassword,
        newPassword,
      });
      results.success = true;
      results.data = (await fetchUserAttributes())['email'];
    } catch (error) {
      results.success = false;
      results.error = error;
    }
    return results;
  }

  async socialLogin(provider: string) {
    try {
      const userInfo = await getCurrentUser();
      if (!userInfo) {
        await signIn({ username: provider });
      }
    } catch (error) {
      // Handle error
    }
  }

  async socialLogout() {}

  async trackLogin(user) {
    const trackingData: any = {};
    trackingData.userAgent = window.navigator.userAgent;
    trackingData.timeStamp = Date.now();

    await this.httpService.post_promise('api/user/track-login', trackingData, loaderTypes.none);
  }

  async closeAccount(input) {
    const userInfo = await getCurrentUser();
    if (userInfo) {
      return this.httpService.post_promise('api/user/close-account', { ...input, cognito_userName: userInfo.username }, loaderTypes.none);
    } else {
      throw new Error('User not found');
    }
  }

  checkWhiteList(request: Object) {
    const url = environment.apiUrl + `/api/user/check-user-whitelist`;
    return this.http.post(url, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response;
        }
      })
    );
  }

  isInGuardianMode(): Promise<boolean> {
    return new Promise((resolve) => {
      this.loginService.loginObject.subscribe(async (state) => {
        try {
          const loginStateObject = state?.data;
          const authUser = await getCurrentUser();
          const attributes = await fetchUserAttributes();
          const isGuardianMode = !!(loginStateObject?.switch_from_user_id && loginStateObject?.id !== attributes['custom:userid']);
          resolve(isGuardianMode);
        } catch (error) {
          resolve(false);
        }
      });
    });
  }

  logoutWithGuardianCheck(): Observable<any> {
    return new Observable((subscriber) => {
      this.isInGuardianMode().then((isGuardianMode) => {
        if (isGuardianMode) {
          this.userService.logoutFromGuardianMode().subscribe(
            () => {
              this.logout().subscribe(
                (result) => subscriber.next(result),
                (error) => subscriber.error(error),
                () => subscriber.complete()
              );
            },
            (error) => subscriber.error(error)
          );
        } else {
          this.logout().subscribe(
            (result) => subscriber.next(result),
            (error) => subscriber.error(error),
            () => subscriber.complete()
          );
        }
      });
    });
  }

  get recaptchaSiteKey(): string {
    return environment.recaptcha_siteKey;
  }

  async verifyRecaptcha(token: string): Promise<any> {
    const url = environment.apiUrl + '/api/user/recaptcha-verification';
    return this.http.post<any>(url, { token }).toPromise();
  }

  getProfessionalCookie(): boolean {
    return JSON.parse(localStorage.getItem('professional') || 'false');
  }

  getTcCodeCookie(): string | null {
    return localStorage.getItem('tcCode');
  }

  googleSocialSignIn(professional, tcCode, signup) {
    const loginUrl = signInWithRedirect({
      provider: 'Google',
    });
    localStorage.setItem('professional', JSON.stringify(professional));
    localStorage.setItem('tcCode', tcCode);

    // window.location.href = loginUrl;
  }
  async sendOtpAlternate(challengeName, emailAddress, password) {
    const results: any = {};
    results.success = false;
    try {
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  setCookie(name: string, value: string, days: number) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    const secure = 'Secure;'; // Add Secure attribute only in production
    const domain = environment.cookieDomain;
    document.cookie = `${name}=${value};${expires};path=/;domain=${domain};SameSite=Strict;${secure}`;
  }

  clearCookie(name: string) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Strict;`;
  }
}
